import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  Search,
  RefreshCw,
  Plus,
  Eye,
  Pencil,
  Trash2,
  BookMarked,
  ChevronLeft,
  ChevronRight,
  X,
  AlertTriangle,
  Info,
  Library,
} from 'lucide-react';
import { toast } from 'sonner';
import { guardrailsApi, guardrailLibraryApi, eventsApi, adminApi } from '../../services/api';
import { Guardrail, GuardrailLibrary, IEvent, AppUser } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;
const LIB_PAGE_SIZE = 5;

const STATUS_COLORS: Record<string, string> = {
  true: 'bg-green-50 text-green-700 border border-green-200',
  false: 'bg-gray-100 text-gray-500 border border-gray-200',
};

const CATEGORY_COLORS: Record<string, string> = {
  'custom': 'bg-blue-50 text-blue-700 border border-blue-200',
  'content-safety': 'bg-purple-50 text-purple-700 border border-purple-200',
  'topic-restriction': 'bg-orange-50 text-orange-700 border border-orange-200',
  'tone-control': 'bg-green-50 text-green-700 border border-green-200',
  'privacy': 'bg-red-50 text-red-700 border border-red-200',
  'compliance': 'bg-purple-50 text-purple-700 border border-purple-200',
  'tone': 'bg-green-50 text-green-700 border border-green-200',
  'workflow': 'bg-orange-50 text-orange-700 border border-orange-200',
};

function priorityBadgeClass(p: number): string {
  if (p === 1) return 'bg-red-50 text-red-700 border border-red-200';
  if (p === 2) return 'bg-orange-50 text-orange-700 border border-orange-200';
  return 'bg-gray-100 text-gray-600 border border-gray-200';
}

// ── Form State ─────────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  description: string;
  rulesText: string;
  priority: number;
  isActive: boolean;
}

const emptyForm = (): FormState => ({
  name: '',
  description: '',
  rulesText: '',
  priority: 1,
  isActive: true,
});

// ── Save to Library Form ───────────────────────────────────────────────────────

interface LibraryForm {
  name: string;
  description: string;
  category: GuardrailLibrary['category'];
}

// ── Component ──────────────────────────────────────────────────────────────────

const Guardrails: React.FC = () => {
  const { isAdmin } = useAuth();

  // Data
  const [guardrails, setGuardrails] = useState<Guardrail[]>([]);
  const [clients, setClients] = useState<AppUser[]>([]);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [libraryTemplates, setLibraryTemplates] = useState<GuardrailLibrary[]>([]);

  // Selectors
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedEventId, setSelectedEventId] = useState<string>('');

  // Loading
  const [loading, setLoading] = useState(false);

  // Search + pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Add/Edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewTarget, setViewTarget] = useState<Guardrail | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState<Guardrail | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Library modal
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libPage, setLibPage] = useState(1);

  // Save to library modal
  const [saveLibOpen, setSaveLibOpen] = useState(false);
  const [saveLibTarget, setSaveLibTarget] = useState<Guardrail | null>(null);
  const [saveLibForm, setSaveLibForm] = useState<LibraryForm>({ name: '', description: '', category: 'custom' });
  const [savingLib, setSavingLib] = useState(false);

  // ── Data fetching ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isAdmin) {
      fetchClients();
    } else {
      fetchEvents('');
    }
    fetchLibraryTemplates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && selectedClientId) {
      fetchEvents(selectedClientId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, isAdmin]);

  const fetchClients = async () => {
    try {
      const res = await adminApi.getAllUsers({ role: 'client', limit: 1000, isActive: true });
      if (res.data.success) setClients(res.data.data?.users || []);
    } catch {
      toast.error('Failed to fetch clients');
    }
  };

  const fetchEvents = async (clientId: string) => {
    try {
      const params: Record<string, any> = { limit: 100 };
      if (isAdmin && clientId) params.clientId = clientId;
      const res = await eventsApi.getAll(params);
      const evts: IEvent[] = res.data.data || [];
      setEvents(evts);
      setSelectedEventId(evts.length > 0 ? evts[0].eventId : '');
    } catch {
      toast.error('Failed to fetch events');
    }
  };

  const fetchGuardrails = useCallback(async () => {
    if (!selectedEventId) return;
    setLoading(true);
    try {
      const res = await guardrailsApi.getByEvent(selectedEventId);
      if (res.data.success) setGuardrails(res.data.data || []);
    } catch {
      toast.error('Failed to fetch guardrails');
    } finally {
      setLoading(false);
    }
  }, [selectedEventId]);

  useEffect(() => {
    fetchGuardrails();
    setCurrentPage(1);
    setSearchQuery('');
  }, [fetchGuardrails]);

  const fetchLibraryTemplates = async () => {
    try {
      const res = await guardrailLibraryApi.getAll({ isPublic: true, limit: 100 });
      if (res.data.success) setLibraryTemplates(res.data.data || []);
    } catch {
      console.error('Failed to fetch library templates');
    }
  };

  // ── Filtering + pagination ───────────────────────────────────────────────────

  const filtered = guardrails.filter((g) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return g.name.toLowerCase().includes(q) || g.description.toLowerCase().includes(q);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const libTotalPages = Math.max(1, Math.ceil(libraryTemplates.length / LIB_PAGE_SIZE));
  const libPaginated = libraryTemplates.slice((libPage - 1) * LIB_PAGE_SIZE, libPage * LIB_PAGE_SIZE);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const openAdd = () => {
    if (!selectedEventId) { toast.warning('Please select an event first'); return; }
    setIsEditing(false);
    setEditingId(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (g: Guardrail) => {
    setIsEditing(true);
    setEditingId(g._id);
    setForm({
      name: g.name,
      description: g.description,
      rulesText: Array.isArray(g.rules) ? g.rules.join('\n') : g.rules,
      priority: g.priority,
      isActive: g.isActive,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Guardrail name is required'); return; }
    if (!form.description.trim()) { toast.error('Description is required'); return; }
    if (!form.rulesText.trim()) { toast.error('At least one rule is required'); return; }

    const rules = form.rulesText
      .split('\n')
      .map((l) => l.trim())
      .filter((l) => l !== '');

    setSaving(true);
    try {
      if (isEditing && editingId) {
        const res = await guardrailsApi.update(editingId, {
          name: form.name.trim(),
          description: form.description.trim(),
          rules,
          priority: form.priority,
          isActive: form.isActive,
        });
        if (res.data.success) {
          toast.success('Guardrail updated successfully');
          closeModal();
          fetchGuardrails();
        } else {
          toast.error(res.data.message || 'Failed to update guardrail');
        }
      } else {
        const res = await guardrailsApi.create({
          eventId: selectedEventId,
          name: form.name.trim(),
          description: form.description.trim(),
          rules,
          priority: form.priority,
          isActive: form.isActive,
        });
        if (res.data.success) {
          toast.success('Guardrail created successfully');
          closeModal();
          fetchGuardrails();
        } else {
          toast.error(res.data.message || 'Failed to create guardrail');
        }
      }
    } catch {
      toast.error(`Failed to ${isEditing ? 'update' : 'create'} guardrail`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await guardrailsApi.delete(deleteTarget._id);
      if (res.data.success) {
        toast.success('Guardrail deleted successfully');
        setDeleteTarget(null);
        fetchGuardrails();
      } else {
        toast.error(res.data.message || 'Failed to delete guardrail');
      }
    } catch {
      toast.error('Failed to delete guardrail');
    } finally {
      setDeleting(false);
    }
  };

  const handleUseTemplate = (tpl: GuardrailLibrary) => {
    if (!selectedEventId) { toast.warning('Please select an event first'); return; }
    setLibraryOpen(false);
    setIsEditing(false);
    setEditingId(null);
    setForm({
      name: tpl.name,
      description: tpl.description,
      rulesText: tpl.rules.join('\n'),
      priority: tpl.priority || 1,
      isActive: true,
    });
    setModalOpen(true);
    guardrailLibraryApi.incrementUsage(tpl._id).catch(() => {});
  };

  const openSaveToLibrary = (g: Guardrail) => {
    setSaveLibTarget(g);
    setSaveLibForm({ name: g.name, description: g.description, category: 'custom' });
    setSaveLibOpen(true);
  };

  const handleSaveToLibrary = async () => {
    if (!saveLibTarget) return;
    if (!saveLibForm.name.trim()) { toast.error('Template name is required'); return; }
    setSavingLib(true);
    try {
      const res = await guardrailLibraryApi.create({
        name: saveLibForm.name.trim(),
        description: saveLibForm.description.trim(),
        rules: saveLibTarget.rules,
        category: saveLibForm.category,
        tags: [],
        priority: saveLibTarget.priority,
        isPublic: true,
        createdBy: undefined,
      });
      if (res.data.success) {
        toast.success('Guardrail saved to library');
        setSaveLibOpen(false);
        setSaveLibTarget(null);
        fetchLibraryTemplates();
      } else {
        toast.error(res.data.message || 'Failed to save to library');
      }
    } catch {
      toast.error('Failed to save to library');
    } finally {
      setSavingLib(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Guardrails</h1>
        <p className="text-sm text-gray-500 mt-1">
          Define rules and constraints to control assistant behaviour
        </p>
      </div>

      {/* Table Card */}
      <div className="bg-white border border-gray-200 rounded-lg">
        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h2 className="text-base font-semibold text-gray-900">Guardrails Management</h2>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                  placeholder="Search guardrails"
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-52"
                />
              </div>

              {/* Client selector (admin only) */}
              {isAdmin && (
                <select
                  value={selectedClientId}
                  onChange={(e) => { setSelectedClientId(e.target.value); setSelectedEventId(''); }}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white min-w-[160px]"
                >
                  <option value="">All Clients</option>
                  {clients.map((c) => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              )}

              {/* Event selector */}
              <select
                value={selectedEventId}
                onChange={(e) => { setSelectedEventId(e.target.value); setCurrentPage(1); }}
                disabled={isAdmin && !selectedClientId}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white min-w-[180px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {events.length === 0 && <option value="">No events</option>}
                {events.map((ev) => (
                  <option key={ev.eventId} value={ev.eventId}>{ev.name}</option>
                ))}
              </select>

              {/* Use Template From Library */}
              <button
                onClick={() => { setLibPage(1); setLibraryOpen(true); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Library className="w-4 h-4" />
                Use Template From Library
              </button>

              {/* Refresh */}
              <button
                onClick={fetchGuardrails}
                disabled={!selectedEventId}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>

              {/* Add Guardrail */}
              <button
                onClick={openAdd}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Guardrail
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rules Count</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center text-sm text-gray-500">
                    Loading guardrails…
                  </td>
                </tr>
              ) : paginated.length > 0 ? (
                paginated.map((g) => (
                  <tr key={g._id} className="hover:bg-gray-50 transition-colors">
                    {/* Name */}
                    <td className="px-6 py-4 max-w-[200px]">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-500 flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-900 leading-snug">{g.name}</span>
                      </div>
                    </td>

                    {/* Description */}
                    <td className="px-6 py-4 max-w-[280px]">
                      <p className="text-sm text-gray-500 truncate" title={g.description}>
                        {g.description}
                      </p>
                    </td>

                    {/* Rules Count */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        {g.rules.length} {g.rules.length === 1 ? 'rule' : 'rules'}
                      </span>
                    </td>

                    {/* Priority */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${priorityBadgeClass(g.priority)}`}>
                        {g.priority}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[String(g.isActive)]}`}>
                        {g.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>

                    {/* Created */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-500">
                        {new Date(g.createdAt).toLocaleDateString()}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setViewTarget(g)}
                          className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEdit(g)}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openSaveToLibrary(g)}
                          className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Save to Library"
                        >
                          <BookMarked className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(g)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center">
                      <Shield className="w-12 h-12 text-gray-300 mb-3" />
                      <p className="text-gray-500 text-sm">No guardrails found</p>
                      <p className="text-gray-400 text-xs mt-1">
                        {selectedEventId ? 'Add a new guardrail or adjust your search' : 'Select an event to view guardrails'}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {filtered.length === 0
                ? '0 guardrails'
                : `${(currentPage - 1) * PAGE_SIZE + 1}–${Math.min(currentPage * PAGE_SIZE, filtered.length)} of ${filtered.length} guardrail${filtered.length !== 1 ? 's' : ''}`}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4 text-gray-600" />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4 text-gray-600" />
              </button>
              <span className="ml-2 text-sm text-gray-500">{PAGE_SIZE} / page</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add / Edit Modal ──────────────────────────────────────────────────── */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={closeModal} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[92vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">
                {isEditing ? 'Edit Guardrail' : 'Add Guardrail'}
              </h2>
              <button onClick={closeModal} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-6 py-6 space-y-5 flex-1">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <span className="text-red-500 mr-0.5">*</span>Guardrail Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. No Internal Document References"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <span className="text-red-500 mr-0.5">*</span>Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  placeholder="Describe what this guardrail prevents or enforces..."
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                />
              </div>

              {/* Rules */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  <span className="text-red-500 mr-0.5">*</span>Rules
                </label>
                <textarea
                  value={form.rulesText}
                  onChange={(e) => setForm((f) => ({ ...f, rulesText: e.target.value }))}
                  placeholder="Enter one rule per line…"
                  rows={5}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-y"
                />
                <p className="mt-1.5 text-xs text-gray-400">
                  Enter one rule per line. These rules will be used to validate AI responses.
                </p>
              </div>

              {/* Priority + Status */}
              <div className="flex items-start gap-8">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    <span className="text-red-500 mr-0.5">*</span>Priority
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={form.priority}
                    onChange={(e) => {
                      const val = Math.min(10, Math.max(1, Number(e.target.value)));
                      setForm((f) => ({ ...f, priority: val }));
                    }}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <p className="mt-1.5 text-xs text-gray-400">1 = Highest priority, 10 = Lowest priority</p>
                </div>

                <div className="pt-0.5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                    className={`relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors text-sm font-medium select-none ${
                      form.isActive ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    <span>{form.isActive ? 'Active' : 'Inactive'}</span>
                    <span className={`relative inline-block w-9 h-5 rounded-full transition-colors ${form.isActive ? 'bg-blue-300' : 'bg-gray-300'}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.isActive ? 'translate-x-4' : 'translate-x-0'}`} />
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving…' : isEditing ? 'Save Changes' : 'Add Guardrail'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation ───────────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">Delete Guardrail</h3>
                <p className="text-sm text-gray-600">
                  Are you sure you want to delete{' '}
                  <span className="font-medium text-gray-900">"{deleteTarget.name}"</span>?
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  This action cannot be undone. The guardrail and all its rules will be permanently removed.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting…' : 'Delete Guardrail'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Library Modal ─────────────────────────────────────────────────────── */}
      {libraryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setLibraryOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col max-h-[90vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Library className="w-5 h-5 text-gray-700" />
                <h2 className="text-base font-semibold text-gray-900">Guardrail Library</h2>
              </div>
              <button onClick={() => setLibraryOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1">
              {/* Info banner */}
              <div className="mx-6 mt-5 mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Info className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-900">Select a template to quickly create a new guardrail</p>
                  <p className="text-sm text-blue-700 mt-0.5">Templates are pre-configured guardrails that you can customize for your Knowledge Assistant.</p>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Description</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                      <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Rules</th>
                      <th className="px-6 py-3 text-center text-sm font-semibold text-gray-700">Usage</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {libPaginated.map((tpl) => (
                      <tr key={tpl._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 max-w-[160px]">
                          <span className="text-sm text-gray-900">{tpl.name}</span>
                        </td>
                        <td className="px-6 py-4 max-w-[200px]">
                          <span className="text-sm text-gray-500" title={tpl.description}>
                            {tpl.description.length > 40 ? tpl.description.slice(0, 40) + '…' : tpl.description}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${CATEGORY_COLORS[tpl.category] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                            {tpl.category}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center whitespace-nowrap">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                            {tpl.rules.length}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center whitespace-nowrap">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200">
                            {tpl.usageCount}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleUseTemplate(tpl)}
                            className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                          >
                            Use Template
                          </button>
                        </td>
                      </tr>
                    ))}
                    {libPaginated.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-6 py-10 text-center text-sm text-gray-500">
                          No templates available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer — pagination + close */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setLibPage((p) => Math.max(1, p - 1))}
                  disabled={libPage === 1}
                  className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                </button>
                {Array.from({ length: libTotalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setLibPage(page)}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                      page === libPage
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setLibPage((p) => Math.min(libTotalPages, p + 1))}
                  disabled={libPage === libTotalPages}
                  className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
                <span className="ml-1 text-sm text-gray-500">{LIB_PAGE_SIZE} / page</span>
              </div>
              <button
                onClick={() => setLibraryOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Save to Library Modal ─────────────────────────────────────────────── */}
      {saveLibOpen && saveLibTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSaveLibOpen(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <BookMarked className="w-5 h-5 text-gray-700" />
                <h2 className="text-base font-semibold text-gray-900">Save to Library</h2>
              </div>
              <button onClick={() => setSaveLibOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-6 space-y-5">
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                <p className="text-sm text-blue-900">Saving <span className="font-medium">"{saveLibTarget.name}"</span> as a reusable template.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Template Name</label>
                <input
                  type="text"
                  value={saveLibForm.name}
                  onChange={(e) => setSaveLibForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                <textarea
                  value={saveLibForm.description}
                  onChange={(e) => setSaveLibForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Category</label>
                <select
                  value={saveLibForm.category}
                  onChange={(e) => setSaveLibForm((f) => ({ ...f, category: e.target.value as GuardrailLibrary['category'] }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                >
                  <option value="custom">Custom</option>
                  <option value="content-safety">Content Safety</option>
                  <option value="topic-restriction">Topic Restriction</option>
                  <option value="tone-control">Tone Control</option>
                  <option value="privacy">Privacy</option>
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setSaveLibOpen(false)}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveToLibrary}
                disabled={savingLib}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {savingLib ? 'Saving…' : 'Save to Library'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Details Modal ───────────────────────────────────────────────── */}
      {viewTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setViewTarget(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">Guardrail Details</h2>
              <button onClick={() => setViewTarget(null)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="overflow-y-auto px-6 py-6 space-y-4 text-sm">
              <div><span className="text-gray-500">Name:</span> <span className="text-gray-900">{viewTarget.name}</span></div>
              <div><span className="text-gray-500">Description:</span> <span className="text-gray-900">{viewTarget.description || '–'}</span></div>
              <div><span className="text-gray-500">Priority:</span> <span className="text-gray-900">{viewTarget.priority}</span></div>
              <div><span className="text-gray-500">Status:</span> <span className="text-gray-900">{viewTarget.isActive ? 'Active' : 'Inactive'}</span></div>
              <div><span className="text-gray-500">Created:</span> <span className="text-gray-900">{new Date(viewTarget.createdAt).toLocaleString()}</span></div>
              <div>
                <p className="text-gray-500 mb-1">Rules:</p>
                <ul className="list-disc pl-5 space-y-1 text-gray-900">
                  {viewTarget.rules.map((r, idx) => <li key={`${viewTarget._id}-rule-${idx}`}>{r}</li>)}
                </ul>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => setViewTarget(null)}
                className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => { const next = viewTarget; setViewTarget(null); openEdit(next); }}
                className="px-6 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Guardrails;
